import * as THREE from "https://esm.sh/three@0.170.0/es2022/three.mjs";
import { GLTFLoader } from "https://esm.sh/three@0.170.0/es2022/examples/jsm/loaders/GLTFLoader.mjs";
import { MeshoptDecoder } from "https://esm.sh/three@0.170.0/examples/jsm/libs/meshopt_decoder.module.js";

const FRONT_YAW = 0;
const TURN = Math.PI * 2;
const DISTANCE = 4.9;

class MvdKitCanvas extends HTMLElement {
  connectedCallback() {
    if (this._started) return;
    this._started = true;
    this.canvas = this.querySelector("[data-kit-view]");
    this.primary = this.dataset.primary || "#1f7a3a";
    this.secondary = this.dataset.secondary || "#79c7ff";
    this.nameText = "";
    this.numberText = "";
    this.playing = false;
    this.yaw = Math.PI * 1.15;
    this.pitch = 0.08;
    this.lookY = 0.92;
    this.distance = DISTANCE;
    this.baseDistance = DISTANCE;
    this.minDistance = 1.6;
    this.maxDistance = 9;
    this._dragging = false;
    this._pointers = new Map();
    this._pinchStart = 0;
    this._pinchDist = DISTANCE;
    this._build();
    this._bind();
    this._playToFront();
    this._loop();
  }

  disconnectedCallback() {
    cancelAnimationFrame(this._frame);
    this._resize?.disconnect();
    this._renderer?.dispose();
    this._started = false;
  }

  setLook({ primary, secondary, name, number }) {
    if (primary) this.primary = primary;
    if (secondary) this.secondary = secondary;
    if (name !== undefined) this.nameText = name;
    if (number !== undefined) this.numberText = number;
    this._paintGarment();
    this._paintDecals();
  }

  _build() {
    this._renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      preserveDrawingBuffer: true,
      alpha: false,
    });
    this._renderer.setClearColor(0xf3f3f3, 1);
    this._renderer.outputColorSpace = THREE.SRGBColorSpace;
    this._renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this._maxAniso = this._renderer.capabilities.getMaxAnisotropy();

    this._scene = new THREE.Scene();
    this._camera = new THREE.PerspectiveCamera(26, 1, 0.05, 50);

    this._scene.add(new THREE.HemisphereLight(0xffffff, 0xd8d8d8, 1.25));
    const key = new THREE.DirectionalLight(0xffffff, 1.35);
    key.position.set(1.6, 3.4, 3.2);
    this._scene.add(key);
    const fill = new THREE.DirectionalLight(0xffffff, 0.55);
    fill.position.set(-2.4, 1.6, 1.2);
    this._scene.add(fill);

    this.rig = new THREE.Group();
    this._scene.add(this.rig);

    this._resize = new ResizeObserver(() => this._fit());
    this._resize.observe(this.canvas.parentElement);
    this._fit();
    this._loadModel();
  }

  _loadModel() {
    const url = this.dataset.model;
    if (!url) return;
    const loader = new GLTFLoader();
    loader.setMeshoptDecoder(MeshoptDecoder);
    const clean = url.split("?")[0];
    const base = clean.slice(0, clean.lastIndexOf("/") + 1);
    const file = clean.slice(clean.lastIndexOf("/") + 1);
    loader.setPath(base);
    loader.load(
      file,
      (gltf) => {
        this.model = gltf.scene;
        this.model.traverse((obj) => {
          if (!obj.isMesh) return;
          const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
          mats.forEach((mat) => {
            ["map", "normalMap", "metalnessMap", "roughnessMap", "aoMap"].forEach((key) => {
              if (!mat?.[key]) return;
              mat[key].anisotropy = this._maxAniso || 1;
              mat[key].colorSpace = key === "map" ? THREE.SRGBColorSpace : mat[key].colorSpace;
              mat[key].needsUpdate = true;
            });
          });
        });
        this.rig.add(this.model);
        this._frameModel();
        this._whenTexturesReady(() => {
          this._paintGarment();
          this._paintDecals();
          this.classList.add("is-ready");
        });
      },
      undefined,
      (error) => {
        console.error(error);
      }
    );
  }

  _whenTexturesReady(done) {
    const maps = [];
    this.model.traverse((obj) => {
      if (!obj.isMesh) return;
      const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
      mats.forEach((mat) => {
        if (mat?.map?.image) maps.push(mat.map.image);
      });
    });
    if (!maps.length) {
      done();
      return;
    }
    let left = maps.length;
    const tick = () => {
      left -= 1;
      if (left <= 0) done();
    };
    maps.forEach((image) => {
      if (image.complete || image instanceof ImageBitmap) tick();
      else {
        image.addEventListener("load", tick, { once: true });
        image.addEventListener("error", tick, { once: true });
      }
    });
  }

  _frameModel() {
    const box = new THREE.Box3().setFromObject(this.model);
    const size = box.getSize(new THREE.Vector3());
    const center = box.getCenter(new THREE.Vector3());
    this.lookY = center.y;
    const half = Math.max(size.y, size.x) * 0.58;
    this.baseDistance = half / Math.tan(THREE.MathUtils.degToRad(13));
    this.distance = this.baseDistance;
    this.minDistance = this.baseDistance * 0.35;
    this.maxDistance = this.baseDistance * 2.4;

    this.frontMat = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false });
    this.backMat = new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false });
    this.frontDecal = new THREE.Mesh(new THREE.PlaneGeometry(size.x * 0.34, size.y * 0.08), this.frontMat);
    this.frontDecal.position.set(center.x, box.min.y + size.y * 0.62, box.max.z + 0.02);
    this.backDecal = new THREE.Mesh(new THREE.PlaneGeometry(size.x * 0.38, size.y * 0.12), this.backMat);
    this.backDecal.position.set(center.x, box.min.y + size.y * 0.62, box.min.z - 0.02);
    this.backDecal.rotation.y = Math.PI;
    this.rig.add(this.frontDecal, this.backDecal);
  }

  _paintGarment() {
    if (!this.model) return;
    if (!this._zones) {
      this._zones = { primary: [], secondary: [], textured: [] };
      this.model.traverse((obj) => {
        if (!obj.isMesh) return;
        const label = `${obj.name} ${obj.parent?.name || ""}`;
        const mats = Array.isArray(obj.material) ? obj.material : [obj.material];
        const next = mats.map((mat) => {
          if (!mat) return mat;
          const name = (mat.name || "").toLowerCase();
          const copy = mat.clone();
          copy.name = mat.name;
          copy.userData.map = mat.map || null;
          if (label.includes("Casual_Body") && name === "purple") {
            this._zones.primary.push(copy);
          } else if (label.includes("Casual_Legs") && name === "lightblue") {
            this._zones.secondary.push(copy);
          } else if (copy.map) {
            this._zones.textured.push(copy);
          } else {
            this._zones.primary.push(copy);
          }
          return copy;
        });
        obj.material = Array.isArray(obj.material) ? next : next[0];
      });
    }
    this._zones.primary.forEach((mat) => this._tint(mat, this.primary, false));
    this._zones.secondary.forEach((mat) => this._tint(mat, this.secondary, false));
    this._zones.textured.forEach((mat) => this._recolourTextured(mat));
  }

  _recolourTextured(material) {
    const source = material.userData.map;
    if (!source?.image) {
      material.color.set(this.primary);
      material.map = null;
      material.needsUpdate = true;
      return;
    }
    const key = `${this.primary}|${this.secondary}`;
    if (material.userData.recolourKey === key && material.map && material.map !== source) {
      material.color.set(0xffffff);
      return;
    }
    const next = recolourKitMap(source, this.primary, this.secondary);
    next.anisotropy = this._maxAniso || 1;
    if (material.map && material.map !== source) material.map.dispose();
    material.map = next;
    material.color.set(0xffffff);
    material.roughness = 0.78;
    material.metalness = 0.05;
    material.userData.recolourKey = key;
    material.needsUpdate = true;
  }

  _tint(material, hex, keepMap) {
    material.color.set(hex);
    if (keepMap) {
      material.map = material.userData.map || material.map;
    } else {
      material.map = null;
    }
    material.roughness = 0.72;
    material.metalness = 0.02;
    material.needsUpdate = true;
  }

  _paintDecals() {
    if (!this.frontMat || !this.backMat) return;
    const ink = inkFor(this.primary);
    replaceMap(this.frontMat, decalTexture(this.numberText, "", ink, 220));
    replaceMap(this.backMat, decalTexture(this.numberText, this.nameText, ink, 240));
    this.frontDecal.visible = this.numberText.trim() !== "";
    this.backDecal.visible = this.numberText.trim() !== "" || this.nameText.trim() !== "";
  }

  _fit() {
    const stage = this.canvas.parentElement;
    const width = stage.clientWidth || 600;
    const height = stage.clientHeight || 600;
    this._renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this._renderer.setSize(width, height, false);
    this._camera.aspect = width / height;
    this._camera.updateProjectionMatrix();
  }

  _frameCamera() {
    const cos = Math.cos(this.pitch);
    this._camera.position.set(
      Math.sin(this.yaw) * cos * this.distance,
      this.lookY + Math.sin(this.pitch) * this.distance * 0.35,
      Math.cos(this.yaw) * cos * this.distance
    );
    this._camera.lookAt(0, this.lookY, 0);
  }

  _playToFront() {
    const mod = ((this.yaw % TURN) + TURN) % TURN;
    const front = ((FRONT_YAW % TURN) + TURN) % TURN;
    let remaining = front - mod;
    if (remaining < 0.04) remaining += TURN;
    this._spinTarget = this.yaw + remaining;
    this.playing = true;
    this.querySelector('[data-action="rotate"]')?.setAttribute("aria-pressed", "true");
  }

  _stopPlay() {
    this.playing = false;
    this.querySelector('[data-action="rotate"]')?.setAttribute("aria-pressed", "false");
  }

  _faceFront() {
    this.yaw = FRONT_YAW;
    this.pitch = 0;
    this.distance = this.baseDistance || DISTANCE;
    this._stopPlay();
  }

  _zoomBy(factor) {
    this.distance = clamp(this.distance * factor, this.minDistance, this.maxDistance);
  }

  _bind() {
    const playBtn = this.querySelector('[data-action="rotate"]');
    const resetBtn = this.querySelector('[data-action="reset"]');
    const saveBtn = this.querySelector('[data-action="save"]');

    playBtn?.addEventListener("click", () => {
      if (this.playing) {
        this._stopPlay();
        return;
      }
      this._playToFront();
    });
    resetBtn?.addEventListener("click", () => this._faceFront());
    saveBtn?.addEventListener("click", () => {
      const link = document.createElement("a");
      link.href = this.canvas.toDataURL("image/png");
      link.download = "kit-preview.png";
      link.click();
    });

    this.canvas.addEventListener(
      "wheel",
      (event) => {
        event.preventDefault();
        const factor = event.deltaY > 0 ? 1.08 : 0.92;
        this._zoomBy(factor);
      },
      { passive: false }
    );

    this.canvas.addEventListener("pointerdown", (event) => {
      this._pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      this.canvas.setPointerCapture(event.pointerId);
      this._stopPlay();
      if (this._pointers.size === 1) {
        this._dragging = true;
        this._lastX = event.clientX;
        this._lastY = event.clientY;
      } else if (this._pointers.size === 2) {
        this._dragging = false;
        const pts = [...this._pointers.values()];
        this._pinchStart = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        this._pinchDist = this.distance;
      }
    });
    this.canvas.addEventListener("pointermove", (event) => {
      if (!this._pointers.has(event.pointerId)) return;
      this._pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
      if (this._pointers.size === 2) {
        const pts = [...this._pointers.values()];
        const gap = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        if (this._pinchStart > 0) {
          this.distance = clamp(
            this._pinchDist * (this._pinchStart / Math.max(gap, 1)),
            this.minDistance,
            this.maxDistance
          );
        }
        return;
      }
      if (!this._dragging) return;
      const dx = event.clientX - this._lastX;
      const dy = event.clientY - this._lastY;
      this._lastX = event.clientX;
      this._lastY = event.clientY;
      this.yaw -= dx * 0.008;
      this.pitch = clamp(this.pitch + dy * 0.004, -0.35, 0.55);
    });
    const stop = (event) => {
      this._pointers.delete(event.pointerId);
      if (this._pointers.size < 2) this._pinchStart = 0;
      if (this._pointers.size === 0) this._dragging = false;
      if (this._pointers.size === 1) {
        const left = [...this._pointers.values()][0];
        this._dragging = true;
        this._lastX = left.x;
        this._lastY = left.y;
      }
    };
    this.canvas.addEventListener("pointerup", stop);
    this.canvas.addEventListener("pointercancel", stop);
  }

  _loop() {
    const tick = () => {
      this._frame = requestAnimationFrame(tick);
      if (this.playing && !this._dragging && this._pointers.size < 2) {
        const left = this._spinTarget - this.yaw;
        if (left <= 0.03) {
          this._faceFront();
        } else {
          this.yaw += Math.min(0.03, left);
          this.pitch *= 0.92;
        }
      }
      this._frameCamera();
      this._renderer.render(this._scene, this._camera);
    };
    tick();
  }
}

class MvdKitColors extends HTMLElement {
  connectedCallback() {
    this.querySelectorAll(".mvd-kit-zone").forEach((zone) => {
      zone.querySelectorAll("button").forEach((button) => {
        button.addEventListener("click", () => this._choose(zone, button));
      });
    });
    this.querySelector("[data-kit-name]")?.addEventListener("input", () => this._sync());
    this.querySelector("[data-kit-number]")?.addEventListener("input", () => this._sync());
    this._sync();
  }

  _choose(zone, button) {
    zone.querySelectorAll("button").forEach((item) => {
      item.setAttribute("aria-pressed", item === button ? "true" : "false");
    });
    const name = zone.querySelector("[data-zone-name]");
    const input = zone.querySelector("[data-zone-input]");
    if (name) name.textContent = button.dataset.label;
    if (input) input.value = `${button.dataset.label} (${button.dataset.hex})`;
    this._sync();
  }

  _sync() {
    const canvas = document.querySelector("mvd-kit-canvas");
    if (!canvas?.setLook) return;
    const zones = [...this.querySelectorAll(".mvd-kit-zone")];
    const picked = (zone) => zone?.querySelector('[aria-pressed="true"]')?.dataset.hex;
    canvas.setLook({
      primary: picked(zones[0]),
      secondary: picked(zones[1]),
      name: this.querySelector("[data-kit-name]")?.value || "",
      number: this.querySelector("[data-kit-number]")?.value || "",
    });
  }
}

function recolourKitMap(source, primaryHex, secondaryHex) {
  const image = source.image;
  const width = image.width || image.videoWidth || 1024;
  const height = image.height || image.videoHeight || 1024;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(image, 0, 0, width, height);
  const frame = ctx.getImageData(0, 0, width, height);
  const pixels = frame.data;
  const primary = hexRgb(primaryHex);
  const secondary = hexRgb(secondaryHex);

  for (let i = 0; i < pixels.length; i += 4) {
    const r = pixels[i];
    const g = pixels[i + 1];
    const b = pixels[i + 2];
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    const sat = max === 0 ? 0 : (max - min) / max;
    const purple =
      sat > 0.18 &&
      lum > 0.06 &&
      lum < 0.9 &&
      b > g * 1.08 &&
      (r > g * 0.85 || b > r * 0.9);
    const light = lum > 0.68 && sat < 0.28;

    if (purple) {
      const shade = Math.min(1.45, 0.35 + lum * 1.2);
      pixels[i] = clampByte(primary.r * shade);
      pixels[i + 1] = clampByte(primary.g * shade);
      pixels[i + 2] = clampByte(primary.b * shade);
    } else if (light) {
      const shade = Math.min(1.2, 0.55 + lum * 0.7);
      pixels[i] = clampByte(secondary.r * shade);
      pixels[i + 1] = clampByte(secondary.g * shade);
      pixels[i + 2] = clampByte(secondary.b * shade);
    }
  }

  ctx.putImageData(frame, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = source.colorSpace || THREE.SRGBColorSpace;
  texture.flipY = source.flipY;
  texture.wrapS = source.wrapS;
  texture.wrapT = source.wrapT;
  texture.needsUpdate = true;
  return texture;
}

function hexRgb(hex) {
  const value = parseInt(String(hex).replace("#", ""), 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
}

function clampByte(value) {
  return Math.max(0, Math.min(255, Math.round(value)));
}

function replaceMap(material, texture) {
  material.map?.dispose();
  material.map = texture;
  material.needsUpdate = true;
}

function decalTexture(number, name, ink, numberSize) {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 256;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, 512, 256);
  ctx.fillStyle = ink;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  if (number) {
    ctx.font = `700 ${numberSize}px Impact, Arial Black, sans-serif`;
    ctx.fillText(number.slice(0, 3), 256, name ? 100 : 128);
  }
  if (name) {
    ctx.font = "700 42px Arial, sans-serif";
    ctx.fillText(name.toUpperCase().slice(0, 16), 256, number ? 190 : 128);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
}

function inkFor(hex) {
  const value = parseInt(hex.replace("#", ""), 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? "#161616" : "#ffffff";
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

if (!customElements.get("mvd-kit-canvas")) {
  customElements.define("mvd-kit-canvas", MvdKitCanvas);
}
if (!customElements.get("mvd-kit-colors")) {
  customElements.define("mvd-kit-colors", MvdKitColors);
}
